// CliAuth.tsx — browser half of the WS-based CLI device-auth flow.
import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'
import Spinner from 'react-bootstrap/Spinner'
import { BsArrowClockwise, BsCheck2, BsX } from 'react-icons/bs'
import { useAuth, apiFetch } from '../hooks/useAuth'

const CliAuth = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { email, loading, error: authError } = useAuth()
    const [status, setStatus] = useState<'idle' | 'approving' | 'approved' | 'failed'>('idle')
    const [errMsg, setErrMsg] = useState('')

    useEffect(() => {
        if (!loading && !email) {
            const returnTo = `/cli-auth/${id}`
            navigate(`/login?return_to=${encodeURIComponent(returnTo)}`, { replace: true })
        }
    }, [loading, email, id, navigate])

    const handleApprove = async () => {
        if (!id) return
        setStatus('approving')
        setErrMsg('')
        try {
            const res = await apiFetch(`/api/cli-auth/${encodeURIComponent(id)}/approve`, { method: 'POST' })
            if (!res.ok) {
                const data = await res.json().catch(() => ({}))
                throw new Error(data.error || `HTTP ${res.status}`)
            }
            setStatus('approved')
        } catch (err: unknown) {
            setStatus('failed')
            setErrMsg(err instanceof Error ? err.message : 'approve failed')
        }
    }

    if (loading) {
        return (
            <div className="auth-wrapper">
                <Spinner animation="border" />
            </div>
        )
    }

    return (
        <div className="auth-wrapper">
            <Card style={{ width: '540px', maxWidth: '100%' }}>
                <Card.Body>
                    <h3 className="mb-3">Authorize CLI session</h3>
                    {authError && <Alert variant="danger">{authError}</Alert>}

                    {status === 'idle' && (
                        <div className="d-flex flex-column gap-3">
                            <p className="m-0">
                                The authz CLI is requesting a long-lived (90-day) token for{' '}
                                <strong>{email}</strong>.
                            </p>
                            <p className="m-0 text-muted small">
                                If you didn't start an <code>authz login</code> in your terminal, deny this request.
                            </p>
                            <p className="m-0 text-muted small">
                                Session ID: <code>{id}</code>
                            </p>
                            <div className="d-flex gap-2">
                                <Button variant="success" onClick={handleApprove} className="d-flex align-items-center gap-2">
                                    <BsCheck2 /> Approve
                                </Button>
                                <Button variant="outline-danger" onClick={() => navigate('/dashboard')} className="d-flex align-items-center gap-2">
                                    <BsX /> Deny
                                </Button>
                            </div>
                        </div>
                    )}

                    {status === 'approving' && (
                        <div className="d-flex align-items-center gap-2">
                            <Spinner animation="border" size="sm" />
                            <span>Approving…</span>
                        </div>
                    )}

                    {status === 'approved' && (
                        <Alert variant="success" className="mb-0">
                            <Alert.Heading className="h6">LOGGED IN</Alert.Heading>
                            Return to your terminal — the CLI should have your token now.
                            You can close this tab.
                        </Alert>
                    )}

                    {status === 'failed' && (
                        <div className="d-flex flex-column gap-3">
                            <Alert variant="danger" className="mb-0">
                                <Alert.Heading className="h6">APPROVE FAILED</Alert.Heading>
                                <div className="small">{errMsg}</div>
                            </Alert>
                            <div>
                                <Button variant="secondary" onClick={() => setStatus('idle')} className="d-flex align-items-center gap-2">
                                    <BsArrowClockwise /> Try again
                                </Button>
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    )
}

export default CliAuth
